import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export type RegisterIgdKolomKode =
  | "asal_rujukan"
  | "pengantar"
  | "penyebab"
  | "kategori"
  | "tindak_lanjut";

export interface RegisterIgdNilai {
  asal_rujukan: string | null;
  pengantar: string | null;
  penyebab: string | null;
  kategori: string | null;
  tindak_lanjut: string | null;
  meninggal: boolean | null;
  keterangan: string | null;
}

export interface RegisterIgdPetunjuk {
  asal_pendaftaran: string | null;
  kedatangan: string | null;
  kasus: string | null;
  triase: string | null;
  disposisi: string | null;
  doa: boolean;
}

export interface RegisterIgdRow {
  register_id: number;
  no: number;
  tgl: string;
  tgl_label: string;
  jam: string;
  rm: string;
  nama: string;
  alamat: string;
  jk: "L" | "P" | null;
  umur: string;
  kunjungan: "B" | "L" | null;
  pembayaran: string | null;
  cara_bayar: string | null;
  diagnosa: string | null;
  diagnosa_sumber: "icd" | "asesmen" | null;
  tindakan: string | null;
  tindakan_sumber: "icd" | "asesmen" | null;
  dokter: string | null;
  petunjuk: RegisterIgdPetunjuk;
  rme: RegisterIgdNilai;
  koreksi: RegisterIgdNilai;
  nilai: RegisterIgdNilai;
  koreksi_oleh: { nama: string | null; pada: string | null } | null;
}

export interface RegisterIgdPenyebabGrup {
  label: string;
  kode: string[];
}

export interface RegisterIgdOpsi {
  asal_rujukan: Record<string, string>;
  pengantar: Record<string, string>;
  penyebab: Record<string, string>;
  penyebab_grup: Record<string, RegisterIgdPenyebabGrup>;
  kategori: Record<string, string>;
  tindak_lanjut: Record<string, string>;
  pembayaran: Record<string, string>;
  kunjungan: Record<string, string>;
}

export interface RegisterIgdMeta {
  total: number;
  belum_lengkap: Record<RegisterIgdKolomKode, number>;
  tanpa_jk: number;
  dikoreksi: number;
}

export interface RegisterIgdParams {
  periode_start: string;
  periode_end: string;
  periode_label: string;
  jumlah_hari: number;
  max_pdf_baris: number;
  max_periode_hari: number;
}

export interface RegisterIgdResponse {
  rows: RegisterIgdRow[];
  meta: RegisterIgdMeta;
  opsi: RegisterIgdOpsi;
  params: RegisterIgdParams;
  boleh_ubah: boolean;
}

export interface RegisterIgdPeriode {
  tgl_start: string;
  tgl_end: string;
}

export interface RegisterIgdKoreksiItem extends RegisterIgdNilai {
  register_id: number;
}

function bacaBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

interface GalatHttp {
  response?: { data?: unknown };
}

async function tampilkanGalat(err: GalatHttp): Promise<never> {
  const data = err.response?.data;

  if (err.response && data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await bacaBlob(data));
    } catch {
      err.response.data = { message: "Berkas gagal dibuat" };
    }
  }

  ErrorAlert.showErrorAlert(err);

  return Promise.reject(err);
}

async function get(p: RegisterIgdPeriode): Promise<RegisterIgdResponse> {
  const res = await http
    .getDataParam(`/laporan/register-igd`, { params: p })
    .catch(tampilkanGalat);

  return res.data;
}

async function save(
  p: RegisterIgdPeriode,
  koreksi: RegisterIgdKoreksiItem[]
): Promise<RegisterIgdResponse> {
  const res = await http
    .putData(`/laporan/register-igd`, { ...p, koreksi })
    .catch(tampilkanGalat);

  return res.data.data;
}

function suffix(p: RegisterIgdPeriode): string {
  return p.tgl_start === p.tgl_end
    ? p.tgl_start
    : `${p.tgl_start}_${p.tgl_end}`;
}

async function openPdf(p: RegisterIgdPeriode): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/register-igd/pdf`, {
      params: p,
      responseType: "blob"
    })
    .catch(tampilkanGalat);

  const url = URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function downloadExcel(p: RegisterIgdPeriode): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/register-igd/excel`, {
      params: p,
      responseType: "blob"
    })
    .catch(tampilkanGalat);

  const url = URL.createObjectURL(
    new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `register-pasien-igd_${suffix(p)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default { get, save, openPdf, downloadExcel };
